import {
    Box,
    Button,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../utils/api";
import { ROUTES } from "../../../utils/routes";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { TABLE_BODY_ROW_MUI_STYLES, TABLE_HEADER_ROW_MUI_STYLES } from "../../../utils/constant";
import CustomPagination from "../../../utils/CustomPagination";
import DeleteModal from "../../../components/dialog/DeleteModal";
import { Alert } from "../../../utils/Alert";

export default function InfluencerSubscriptionTypeList() {
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit]);

    const fetchList = () => {
        const query = `?limit=${limit}&offset=${page * limit}`;
        api.getApiCall("influencer-subscription/type/list/admin", query, (response) => {
            if (response.data && response.data.result) {
                const result = response.data.result;
                setList(Array.isArray(result.list) ? result.list : []);
                setTotalCount(result.count || 0);
            }
        }, (error) => {
            console.error("Failed to fetch types", error);
            setList([]);
            Alert(2, "Failed to fetch types");
        });
    };

    const handleDelete = (id) => {
        setDeleteId(id);
        setOpenDelete(true);
    };

    const confirmDelete = () => {
        api.deleteApiCall(`influencer-subscription/type/delete/${deleteId}`, "", (response) => {
            setOpenDelete(false);
            setDeleteId(null);
            Alert(1, "Type deleted successfully");
            fetchList();
        }, (error) => {
            console.error("Failed to delete", error);
            setOpenDelete(false);
            Alert(2, "Failed to delete type");
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box p={3}>
            <Stack direction="row" alignItems="center" mb={4}>
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    flexWrap="wrap" gap={2}
                    sx={{ minHeight: '42px', paddingX: '16px', width: '100%' }}
                >
                    <Typography className="page-title" variant="h1">Subscription Types</Typography>
                    <Stack direction="row" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={2}>
                        <button className="btnPrimary" onClick={() => navigate(ROUTES.INFLUENCER_SUBSCRIPTION_TYPE_ADD)}>
                            Add New Type
                        </button>
                    </Stack>
                </Stack>
            </Stack>

            <Stack direction={"column"} gap={2}>
                <Box sx={{
                    backgroundColor: '#fff',
                    transition: 'all .5s ease-in-out',
                    position: 'relative',
                    border: '1px solid rgba(170, 170, 170, 0.3)',
                }}>
                    <TableContainer>
                        <Table aria-label="sticky table">
                            <TableHead className={`hideActionSort`}>
                                <TableRow sx={TABLE_HEADER_ROW_MUI_STYLES}>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>Type</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>Base Price</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>Offer %</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>Price</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>Duration (Days)</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>Active</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(list) && list.map((row) => (
                                    <TableRow key={row.id} sx={TABLE_BODY_ROW_MUI_STYLES} hover role="checkbox" tabIndex={-1}>
                                        <TableCell component="td" scope="row">{row.type}</TableCell>
                                        <TableCell component="td" scope="row">{row.basePrice}</TableCell>
                                        <TableCell component="td" scope="row">{row.offerPercentage}%</TableCell>
                                        <TableCell component="td" scope="row">{row.price}</TableCell>
                                        <TableCell component="td" scope="row">{row.duration_days}</TableCell>
                                        <TableCell component="td" scope="row">
                                            {row.isActive ?
                                                <CheckCircleIcon color="success" /> :
                                                <CancelIcon color="disabled" />
                                            }
                                        </TableCell>
                                        <TableCell component="td" scope="row" sx={{ cursor: "pointer" }}>
                                            <Stack direction={"row"} sx={{ gap: '8px' }}>
                                                <div className="actBtnGrps">
                                                    <span className="editIcon" onClick={() => navigate(`${ROUTES.INFLUENCER_SUBSCRIPTION_TYPE_EDIT}/${row.id}`)}>Edit</span>
                                                    <span className="deleteIcon" onClick={() => handleDelete(row.id)}>Delete</span>
                                                </div>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {list.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No subscription types found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Stack>
            <Stack sx={{ background: 'transparent', height: '60px', border: '1px solid #E9EAEB', borderTop: '0px' }} direction="row" alignItems={"center"} justifyContent={"flex-end"}>
                <CustomPagination
                    count={Math.ceil(totalCount / limit) || 0}
                    onChange={(value) => setPage(value - 1)}
                    currentPage={page}
                    handleChangePerPage={handleChangeRowsPerPage}
                    limit={limit}
                    totalCount={totalCount}
                    listingCount={list.length}
                    defaultPage={1} siblingCount={1} boundaryCount={1}
                />
            </Stack>
            <DeleteModal
                open={openDelete}
                setOpen={setOpenDelete}
                handleDelete={confirmDelete}
                title="Delete Subscription Type"
                subHeading="Are you sure you want to delete this subscription type?"
            />
        </Box>
    );
}
